import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
  ArrowBack,
  MailOutline,
} from '@mui/icons-material';
import { motion, AnimatePresence, useReducedMotion, type Variants } from 'framer-motion';
import { CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { tokens } from '../theme/tokens';
import GoogleBrandButton from './login/GoogleBrandButton';
import BrandPanel, { type CatMood } from './login/BrandPanel';

// The public OAuth client id is read at build time; when it is unset we must still render
// (a disabled Google button + helper note) rather than white-screen.
const googleEnabled = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    minHeight: 52,
    backgroundColor: tokens.surface,
    '& fieldset': { borderColor: tokens.border, borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: tokens.muted },
    '&.Mui-focused fieldset': { borderColor: tokens.orange, borderWidth: 2 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: tokens.orange },
};

const LoginPage: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, loginWithGoogle, isLoading, authError, clearAuthError } = useAuth();

  // Google-first UX state.
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  // The most recent Google credential; kept so the reason dialog can resubmit it.
  const [pendingCredential, setPendingCredential] = useState<string | null>(null);
  // When a resubmit fails because the credential expired, we ask the user to sign in with
  // Google again while preserving the typed reason, then auto-resubmit on the next success.
  const [awaitingReauth, setAwaitingReauth] = useState(false);
  // Drives the brand-panel cat: it reacts to hover, loading, errors and success.
  const [reaching, setReaching] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  // Local busy flag for the Google flow. We can't use the context `isLoading` here because
  // that gates ProtectedRoute and would remount this page mid-flow (dropping the reason
  // dialog / pending panel). This keeps the page mounted and still shows a busy state.
  const [googleBusy, setGoogleBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      const errorMsg = 'Please enter both email and password';
      setError(errorMsg);
      return;
    }

    // clear local error and start login
    setError('');
    // clear any persistent auth error before attempting login
    clearAuthError();

    try {
      await login(email, password);
      // AuthContext will set authError on failure; no need to set local error here
    } catch (err) {
      const errorMsg = 'Login failed. Please try again.';
      setError(errorMsg);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const revealPasswordForm = () => {
    setShowPasswordForm(true);
    setPending(null);
  };

  // Submit a Google credential (optionally with a reason) and act on the returned flags.
  const submitGoogle = async (credential: string, reasonValue?: string) => {
    setError('');
    clearAuthError();
    setGoogleBusy(true);
    let result;
    try {
      result = await loginWithGoogle(credential, reasonValue);
    } finally {
      setGoogleBusy(false);
    }

    if (result.ok) {
      setSucceeded(true);
      // AuthProvider stored the session + token; the app re-renders into the protected view.
      setReasonDialogOpen(false);
      setAwaitingReauth(false);
      return;
    }

    if (result.pending) {
      setReasonDialogOpen(false);
      setAwaitingReauth(false);
      setPending(result.message || 'Your access request has been submitted and is pending approval.');
      return;
    }

    if (result.needsReason) {
      // Unknown Salty account — collect a reason and resubmit the same credential.
      setReasonDialogOpen(true);
      return;
    }

    if (result.needsReauth) {
      // The credential expired mid-flow; keep the typed reason and ask for a fresh sign-in.
      setReasonDialogOpen(false);
      setAwaitingReauth(true);
      setPendingCredential(null);
      setError(result.message || 'Your Google sign-in expired. Please sign in with Google again.');
      return;
    }

    // 403 non-Salty or any other failure: show the server message and reveal the password form.
    if (result.message) setError(result.message);
    setShowPasswordForm(true);
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    const credential = credentialResponse.credential;
    if (!credential) {
      setError('Google sign-in did not return a credential. Please try again.');
      return;
    }
    setPendingCredential(credential);
    // If we were waiting for a re-auth after an expiry, auto-resubmit with the preserved reason.
    const reasonValue = awaitingReauth || reasonDialogOpen ? reason.trim() || undefined : undefined;
    setAwaitingReauth(false);
    await submitGoogle(credential, reasonValue);
  };

  const handleGoogleError = () => {
    setError('Google sign-in could not be completed. Please try again.');
  };

  const handleReasonSubmit = async () => {
    if (!reason.trim() || !pendingCredential) return;
    await submitGoogle(pendingCredential, reason.trim());
  };

  // Entrance + stagger variants for the right auth column. Gated behind reduceMotion at the
  // call site via `initial={reduceMotion ? false : 'hidden'}` so reduced motion renders static.
  const columnVariants: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
  };
  const childVariants: Variants = {
    hidden: { opacity: 0, y: 8 },
    show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] } },
  };
  // Smooth height/opacity transition for the reveal + pending swap.
  const collapseTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.28, ease: [0.4, 0, 0.2, 1] as const };

  const mood: CatMood = succeeded
    ? 'success'
    : error || authError
      ? 'error'
      : isLoading
        ? 'loading'
        : reaching
          ? 'hover'
          : 'idle';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        backgroundColor: tokens.surface,
      }}
    >
      <BrandPanel mood={mood} />

      {/* Right auth panel — a warm field with one focused card. */}
      <Box
        component="main"
        sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6 },
          py: { xs: 4, md: 6 },
          backgroundColor: '#F7F7EE',
        }}
      >
        <Box
          component={motion.div}
          variants={columnVariants}
          initial={reduceMotion ? false : 'hidden'}
          animate="show"
          onPointerEnter={() => setReaching(true)}
          onPointerLeave={() => setReaching(false)}
          sx={{
            width: '100%',
            maxWidth: 440,
            p: { xs: 3, sm: 5 },
            borderRadius: '20px',
            bgcolor: tokens.surface,
            border: `1px solid ${tokens.border}`,
            boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 24px 60px rgba(26,20,15,0.08)',
          }}
        >
          <Box component={motion.div} variants={childVariants} sx={{ mb: 4 }}>
            <Typography
              sx={{
                color: tokens.orange,
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                mb: 1.25,
              }}
            >
              Salty WMS
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontWeight: 700,
                color: tokens.ink,
                fontSize: { xs: '1.85rem', md: '2.25rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.035em',
              }}
            >
              {showPasswordForm && !pending ? 'Sign in with email' : 'Welcome back'}
            </Typography>
            <Typography sx={{ color: tokens.muted, mt: 1.25, fontSize: '0.98rem', lineHeight: 1.5 }}>
              {showPasswordForm && !pending
                ? 'Enter the email and password for your Salty account.'
                : 'Sign in with your Salty Google account to continue.'}
            </Typography>
          </Box>

          <AnimatePresence initial={false}>
            {(error || authError) && (
              <Box
                component={motion.div}
                key="auth-error"
                role="alert"
                initial={reduceMotion ? false : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={collapseTransition}
                sx={{ overflow: 'hidden' }}
              >
                <Box
                  component={motion.div}
                  animate={reduceMotion ? undefined : { x: [0, -6, 6, -4, 4, 0] }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                >
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: '12px',
                      border: '1px solid rgba(220,38,38,0.25)',
                      bgcolor: 'rgba(220,38,38,0.06)',
                      color: tokens.ink,
                      '& .MuiAlert-message': { fontWeight: 500 },
                    }}
                  >
                    {error || authError}
                  </Alert>
                </Box>
              </Box>
            )}
          </AnimatePresence>

          {pending ? (
            <Box
              component={motion.div}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={collapseTransition}
              sx={{
                p: 3,
                borderRadius: '16px',
                border: `1px solid ${tokens.border}`,
                bgcolor: tokens.beige,
              }}
            >
              <CheckCircleOutline sx={{ fontSize: 40, color: 'success.main', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', mb: 0.75, color: tokens.ink }}>
                Request submitted
              </Typography>
              <Typography sx={{ color: tokens.muted, mb: 2.5, lineHeight: 1.55 }}>
                {pending}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => {
                  setPending(null);
                  setReason('');
                  setPendingCredential(null);
                }}
                sx={{ borderRadius: '12px', minHeight: 44 }}
              >
                Back to sign in
              </Button>
            </Box>
          ) : (
            <>
              {!showPasswordForm && (
                <Box component={motion.div} variants={childVariants}>
                  {googleEnabled ? (
                    <GoogleBrandButton
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      width={360}
                    />
                  ) : (
                    <Box>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        disabled
                        sx={{ minHeight: 52, borderRadius: '12px' }}
                      >
                        Sign in with Google
                      </Button>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: tokens.muted }}>
                        Google sign-in is not configured. Set REACT_APP_GOOGLE_CLIENT_ID to enable it,
                        or sign in with email and password below.
                      </Typography>
                    </Box>
                  )}

                  {awaitingReauth && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: tokens.muted }}>
                      Your previous sign-in expired. Click the Google button again — your reason has been kept.
                    </Typography>
                  )}

                  <Divider sx={{ my: 2.5, color: tokens.muted, fontSize: '0.8rem', '&::before, &::after': { borderColor: tokens.border } }}>
                    or
                  </Divider>

                  <Button
                    fullWidth
                    variant="text"
                    onClick={revealPasswordForm}
                    startIcon={<MailOutline />}
                    sx={{
                      minHeight: 48,
                      borderRadius: '12px',
                      color: tokens.ink,
                      fontWeight: 600,
                      border: '1px solid transparent',
                      '&:hover': { bgcolor: tokens.beige, borderColor: tokens.border },
                    }}
                  >
                    Continue with email
                  </Button>
                </Box>
              )}

              {/* Existing email/password form — revealed on demand, unchanged behavior */}
              <AnimatePresence initial={false}>
                {showPasswordForm && (
                  <Box
                    component={motion.div}
                    key="password-form"
                    initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={collapseTransition}
                  >
                    <form onSubmit={handleSubmit} autoComplete="on">
                      {/* Hidden fields to mitigate browser autofill/credential UI */}
                      <input
                        type="text"
                        name="fake-username"
                        autoComplete="username"
                        style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
                        aria-hidden
                        tabIndex={-1}
                      />
                      <input
                        type="password"
                        name="fake-password"
                        autoComplete="current-password"
                        style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
                        aria-hidden
                        tabIndex={-1}
                      />

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                          fullWidth
                          type="email"
                          label="Email address"
                          name="email"
                          autoComplete="username"
                          autoFocus
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (error || authError) {
                              setError('');
                              clearAuthError();
                            }
                          }}
                          disabled={isLoading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email sx={{ color: tokens.muted }} />
                              </InputAdornment>
                            ),
                            inputProps: {
                              readOnly: true,
                              onFocus: (e: any) => (e.target.readOnly = false),
                            },
                          }}
                          sx={fieldSx}
                        />

                        <TextField
                          fullWidth
                          type={showPassword ? 'text' : 'password'}
                          label="Password"
                          name="password"
                          autoComplete="current-password"
                          value={password}
                          onKeyUp={(e) => setCapsLock(e.getModifierState('CapsLock'))}
                          onBlur={() => setCapsLock(false)}
                          helperText={capsLock ? 'Caps Lock is on' : undefined}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (error || authError) {
                              setError('');
                              clearAuthError();
                            }
                          }}
                          disabled={isLoading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock sx={{ color: tokens.muted }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleTogglePassword}
                                  edge="end"
                                  disabled={isLoading}
                                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                            inputProps: {
                              readOnly: true,
                              onFocus: (e: any) => (e.target.readOnly = false),
                            },
                          }}
                          sx={fieldSx}
                        />

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={isLoading}
                          sx={{
                            minHeight: 52,
                            borderRadius: '12px',
                            fontWeight: 700,
                            fontSize: '1rem',
                            boxShadow: 'none',
                            '&:hover': { boxShadow: '0 10px 24px rgba(220,88,41,0.32)' },
                          }}
                          startIcon={
                            isLoading ? <CircularProgress size={20} color="inherit" /> : null
                          }
                        >
                          {isLoading ? 'Signing in…' : 'Sign in'}
                        </Button>

                        <Button
                          type="button"
                          fullWidth
                          variant="text"
                          startIcon={<ArrowBack />}
                          onClick={() => {
                            setShowPasswordForm(false);
                            setError('');
                            clearAuthError();
                          }}
                          sx={{ minHeight: 44, borderRadius: '12px', color: tokens.muted, fontWeight: 600 }}
                        >
                          Back to Google sign-in
                        </Button>
                      </Box>
                    </form>
                  </Box>
                )}
              </AnimatePresence>
            </>
          )}

          <Box component={motion.div} variants={childVariants} sx={{ mt: 5 }}>
            <Typography sx={{ color: tokens.muted, fontSize: '0.82rem', lineHeight: 1.6 }}>
              Access is limited to Salty accounts. New to the tool? Sign in with Google and
              you&apos;ll be asked to request access from an admin.
            </Typography>
          </Box>
        </Box>

        <Typography
          sx={{
            position: 'absolute',
            bottom: 20,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: tokens.muted,
            fontSize: '0.75rem',
            letterSpacing: '0.02em',
          }}
        >
          Salty Warehouse Management
        </Typography>
      </Box>

      {/* Reason dialog for an unknown Salty account requesting access */}
      <Dialog
        open={reasonDialogOpen}
        onClose={() => setReasonDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Request access</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You don't have an account yet. Tell the admins why you need access, and your
            request will be reviewed.
          </DialogContentText>
          <TextField
            label="Reason"
            fullWidth
            required
            multiline
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
            inputProps={{ maxLength: 1000 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setReasonDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReasonSubmit}
            variant="contained"
            disabled={!reason.trim() || googleBusy}
          >
            {googleBusy ? 'Submitting...' : 'Submit request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
